import { RRule } from 'rrule'
import { client } from './sanity'

const FREQ_MAP = { DAILY: RRule.DAILY, WEEKLY: RRule.WEEKLY, MONTHLY: RRule.MONTHLY, YEARLY: RRule.YEARLY }
const FREQ_NAMES = { [RRule.DAILY]: 'DAILY', [RRule.WEEKLY]: 'WEEKLY', [RRule.MONTHLY]: 'MONTHLY', [RRule.YEARLY]: 'YEARLY' }
const WEEKDAYS = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU]

function optionsToBareString(options) {
    const full = RRule.optionsToString(options)
    const line = full.split('\n').find(l => l.startsWith('RRULE:'))
    return line ? line.slice('RRULE:'.length) : full
}

/** Build a bare RRULE options string (no DTSTART) from a RecurrenceEditor draft. */
export function buildRRuleString(draft) {
    const options = { freq: FREQ_MAP[draft.freq], interval: Math.max(1, draft.interval || 1) }
    if (draft.freq === 'WEEKLY' && draft.byweekday?.length) {
        options.byweekday = draft.byweekday.map(n => WEEKDAYS[n])
    }
    if (draft.endMode === 'until' && draft.until) options.until = draft.until
    if (draft.endMode === 'count' && draft.count) options.count = draft.count
    return optionsToBareString(options)
}

/** Parse a stored RRULE string back into a RecurrenceEditor draft. */
export function parseRRuleString(rruleString) {
    if (!rruleString) return null
    const parsed = RRule.parseString(rruleString)
    const byweekday = (parsed.byweekday || []).map(wd => (typeof wd === 'number' ? wd : wd.weekday))
    let endMode = 'never', until = null, count = null
    if (parsed.until) { endMode = 'until'; until = parsed.until }
    else if (parsed.count) { endMode = 'count'; count = parsed.count }
    return { freq: FREQ_NAMES[parsed.freq] || 'WEEKLY', interval: parsed.interval || 1, byweekday, endMode, until, count }
}

/**
 * @typedef {Object} OccurrenceEvent
 * @property {string} _id - real Sanity _id (standalone/override) OR a synthetic `${masterId}__${isoRecurrenceId}`
 * @property {string|null} masterId - set for any occurrence belonging to a series
 * @property {Date|null} recurrenceId - the ORIGINAL occurrence slot per the master's rule, stable across edits
 * @property {boolean} isOverride
 * @property {string} title
 * @property {string|undefined} description
 * @property {string|undefined} location
 * @property {boolean} allDay
 * @property {Date} start
 * @property {Date} end
 * @property {{_id:string,title:string,color:string,isMilestone:boolean}|null} category
 */

function toOccurrence(doc, extra) {
    return {
        _id: extra._id,
        masterId: extra.masterId,
        recurrenceId: extra.recurrenceId,
        isOverride: extra.isOverride,
        title: doc.title,
        description: doc.description,
        location: doc.location,
        allDay: !!doc.allDay,
        start: extra.start,
        end: extra.end,
        category: doc.category || null,
    }
}

/**
 * Expand raw calendarEvent docs (standalone / master / override) into concrete
 * OccurrenceEvent instances overlapping [rangeStart, rangeEnd]. Client-side, at query time.
 * @returns {OccurrenceEvent[]}
 */
export function expandEvents(rawDocs, rangeStart, rangeEnd) {
    const standalone = []
    const masters = []
    const overrides = []
    for (const doc of rawDocs || []) {
        if (doc.rrule) masters.push(doc)
        else if (doc.recurrenceParent) overrides.push(doc)
        else standalone.push(doc)
    }

    const overrideMap = new Map()
    for (const o of overrides) {
        const masterId = o.recurrenceParent?._ref || o.recurrenceParent?._id
        if (!masterId || !o.recurrenceId) continue
        overrideMap.set(`${masterId}__${new Date(o.recurrenceId).toISOString()}`, o)
    }

    const results = []

    for (const doc of standalone) {
        const start = new Date(doc.start)
        const end = new Date(doc.end)
        if (end < rangeStart || start > rangeEnd) continue
        results.push(toOccurrence(doc, { _id: doc._id, masterId: null, recurrenceId: null, isOverride: false, start, end }))
    }

    for (const doc of masters) {
        const masterStart = new Date(doc.start)
        const masterEnd = new Date(doc.end)
        const duration = masterEnd.getTime() - masterStart.getTime()

        let rule
        try {
            rule = new RRule({ ...RRule.parseString(doc.rrule), dtstart: masterStart })
        } catch {
            continue
        }

        const exceptionSet = new Set((doc.exceptions || []).map(d => new Date(d).toISOString()))
        const occurrenceDates = rule.between(rangeStart, rangeEnd, true)

        for (const occDate of occurrenceDates) {
            const iso = occDate.toISOString()
            if (exceptionSet.has(iso)) continue

            const override = overrideMap.get(`${doc._id}__${iso}`)
            if (override) {
                results.push(toOccurrence(override, {
                    _id: override._id,
                    masterId: doc._id,
                    recurrenceId: occDate,
                    isOverride: true,
                    start: new Date(override.start),
                    end: new Date(override.end),
                }))
            } else {
                results.push(toOccurrence(doc, {
                    _id: `${doc._id}__${iso}`,
                    masterId: doc._id,
                    recurrenceId: occDate,
                    isOverride: false,
                    start: occDate,
                    end: new Date(occDate.getTime() + duration),
                }))
            }
        }
    }

    results.sort((a, b) => a.start - b.start)
    return results
}

/** Truncate a master series so it ends just before `splitAt`, and drop any overrides/exceptions at/after it. */
async function truncateSeriesBefore(masterDoc, splitAt) {
    const parsed = RRule.parseString(masterDoc.rrule)
    const untilDate = new Date(splitAt.getTime() - 1000)
    const truncatedRrule = optionsToBareString({ ...parsed, until: untilDate, count: undefined })

    const remainingExceptions = (masterDoc.exceptions || []).filter(d => new Date(d) < splitAt)

    await client.patch(masterDoc._id).set({ rrule: truncatedRrule, exceptions: remainingExceptions }).commit()

    const orphanIds = await client.fetch(
        `*[_type == "calendarEvent" && recurrenceParent._ref == $id && recurrenceId >= $split]._id`,
        { id: masterDoc._id, split: splitAt.toISOString() }
    )
    await Promise.all(orphanIds.map(id => client.delete(id)))
}

/**
 * "This and following" edit: truncate the old series before `splitAt`, then create
 * a new series starting at `newStart` carrying forward the old rule's freq/interval/byweekday.
 */
export async function splitSeriesAt({ masterId, splitAt, newStart, newEnd, fields }) {
    const masterDoc = await client.getDocument(masterId)
    if (!masterDoc) throw new Error('Recurring series no longer exists.')

    const parsed = RRule.parseString(masterDoc.rrule)
    await truncateSeriesBefore(masterDoc, splitAt)

    let carriedUntil
    if (parsed.until) {
        carriedUntil = parsed.until
    } else if (parsed.count) {
        const oldRule = new RRule({ ...parsed, dtstart: new Date(masterDoc.start) })
        const allDates = oldRule.all()
        carriedUntil = allDates[allDates.length - 1]
    }
    const newRrule = optionsToBareString({
        freq: parsed.freq,
        interval: parsed.interval,
        byweekday: parsed.byweekday,
        ...(carriedUntil ? { until: carriedUntil } : {}),
    })

    await client.create({
        _type: 'calendarEvent',
        ...fields,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
        rrule: newRrule,
    })
}

/** "This and following" delete: truncate the old series before `splitAt`, create nothing new. */
export async function deleteSeriesFrom({ masterId, splitAt }) {
    const masterDoc = await client.getDocument(masterId)
    if (!masterDoc) throw new Error('Recurring series no longer exists.')
    await truncateSeriesBefore(masterDoc, splitAt)
}

/**
 * Move a single occurrence to a new start/end, handling all three occurrence roles:
 * standalone (patch directly), existing override (patch directly), or a computed
 * occurrence of a series (create a new override, never mutate the whole series).
 */
export async function moveOccurrence(event, newStart, newEnd) {
    if (!event.masterId || event.isOverride) {
        await client.patch(event._id).set({ start: newStart.toISOString(), end: newEnd.toISOString() }).commit()
        return
    }
    await client.create({
        _type: 'calendarEvent',
        title: event.title,
        allDay: event.allDay,
        location: event.location,
        description: event.description,
        category: event.category ? { _type: 'reference', _ref: event.category._id } : undefined,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
        recurrenceParent: { _type: 'reference', _ref: event.masterId },
        recurrenceId: event.recurrenceId.toISOString(),
    })
}

/** Delete an entire series: the master doc plus every override referencing it. */
export async function deleteWholeSeries(masterId) {
    const overrideIds = await client.fetch(
        `*[_type == "calendarEvent" && recurrenceParent._ref == $id]._id`,
        { id: masterId }
    )
    await Promise.all(overrideIds.map(id => client.delete(id)))
    await client.delete(masterId)
}
